'use client';
import Link from '@/components/LocaleLink';
import Image from 'next/image';
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { login } from '@/utils/user_service';
import { FacebookIcon, GoogleIcon } from '@/components/icons';
import { signInClicked } from '../../../socialAuthHandles';
import { setLoginReturnUrl } from '@/magicLinkHandle';
import { useTranslation } from '@/contexts/TranslationContext';

function SignUpContent() {
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: '',
    password: '',
    passwordConfirmation: '',
    termsAgreement: false,
  });
  // Persist returnUrl so magic link / OAuth redirect can send user back after signup
  useEffect(() => {
        const returnUrl = searchParams.get('returnUrl');
        if (returnUrl) setLoginReturnUrl(returnUrl);
  }, [searchParams]);
  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const submitForm = (event) => {
    event.preventDefault();
    login(form);
  };

  return (
    <>
      <main>
        <div className="contanier-fluid signin-conatiner">
          <div className="row">
            <div className="col-12 col-md-5">
              <div className="left-panel">
                <h4>{t('auth.signUp')}</h4>
                <Image priority src="/assets/images/logos/NW-round.svg" alt="LM" height="350" width="350" />
              </div>
            </div>
            <div className="col-12 col-md-7">
              <div className="row">
                <div className="col-12 col-lg-9 mx-auto text-center py-3 py-md-5 px-lg-0">
                  <h3 className="d-none d-lg-block">{t('auth.benefits')}</h3>

                  <span className="span-forever">{t('auth.freeForever')}</span>
                  <div className="d-flex my-3 benefits-section">
                    <div>
                      <Image priority src="/assets/images/icons/reminder.svg" alt={t('aria.icon')} width="35" height="35" />
                      <h6>
                        {t('auth.benefits.reminders')} <br /> {t('auth.benefits.reminders2')}
                      </h6>
                    </div>
                    <div>
                      <Image priority src="/assets/images/icons/chart_growth.svg" alt={t('aria.icon')} width="35" height="35" />
                      <h6>
                        {t('auth.benefits.chartGrowth')} <br /> {t('auth.benefits.chartGrowth2')}
                      </h6>
                    </div>
                    <div>
                      <Image priority src="/assets/images/icons/family.svg" alt={t('aria.icon')} width="35" height="35" />
                      <h6>
                        {t('auth.benefits.addFamily')} <br /> {t('auth.benefits.addFamily2')}
                      </h6>
                    </div>
                  </div>

                  <div className="row">
                    <h2 className="mb-4">{t('auth.letsGetSetUp')}</h2>
                    <div className="col-12 col-md-6 d-flex gap-3 justify-content-center w-100">
                      <button className="social-login-btn" onClick={() => signInClicked('google')}>
                        <GoogleIcon />
                        {t('auth.signUpWithGoogle')}
                      </button>
                      <button className="social-login-btn" onClick={() => signInClicked('facebook')}>
                        <FacebookIcon />
                        {t('auth.signUpWithFacebook')}
                      </button>
                    </div>

                    <div className="or-div my-4">
                      <span>{t('auth.or')}</span>
                    </div>
                    <form onSubmit={submitForm}>
                      <div className="col-12">
                        <div className="position-relative">
                          <label className="d-block text-start">{t('auth.name')}</label>
                          <input
                            name="name"
                            placeholder={t('auth.namePlaceholder')}
                            type="text"
                            className="form-control no-border email"
                            onChange={handleInputChange}
                            required
                          />
                          <Image
                            priority
                            className="email-icon"
                            src="/assets/images/icons/person.svg"
                            alt={t('aria.emailIcon')}
                            height="22"
                            width="22"
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="position-relative mt-md-4 mt-3">
                          <label className="d-block text-start">{t('auth.email')}</label>
                          <input
                            name="email"
                            placeholder={t('auth.signupEmailPlaceholder')}
                            type="email"
                            className="form-control no-border email"
                            onChange={handleInputChange}
                            required
                          />
                          <Image
                            priority
                            className="email-icon"
                            src="/assets/images/icons/email.svg"
                            alt={t('aria.emailIcon')}
                            height="22"
                            width="22"
                          />
                        </div>
                      </div>
                      <div className="col-12 my-lg-5 my-5">
                        <p className="text-center guide-text">
                          {t('auth.magicLinkIntro')} <strong>{t('auth.signupEmailPlaceholder')}</strong>
                        </p>

                        <p className="mt-3 text-center guide-text">
                          {t('auth.magicLinkSignUp').split('{action}')[0]}
                          <strong>{t('auth.signUp')}</strong>
                          {t('auth.magicLinkSignUp').split('{action}')[1]}
                        </p>
                      </div>
                      <div className="col-12">
                        <button type="submit" className="btn btn-primary btn-lg btn-yello">
                          {t('auth.signUp')}
                        </button>
                      </div>
                    </form>
                    <p className="my-4" style={{ fontWeight: '400' }}>
                      {t('auth.alreadyHaveAccount')}
                        <Link
                          prefetch={false}
                          href={searchParams.get('returnUrl') ? `/signin?returnUrl=${encodeURIComponent(searchParams.get('returnUrl'))}` : '/signin'}
                          style={{ color: "#FFB727", coursor: "pointer" }}
                        >
                        {t('auth.logIn')}
                      </Link>
                    </p>
                    <Link prefetch={false} href="/" className="home-link mt-1 mb-4">
                      {t('auth.homepageLink')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

function SignUpFallback() {
    const { t } = useTranslation();
    return (
        <main>
            <div className="contanier-fluid signin-conatiner">
                <div className="row">
                    <div className="col-12 col-md-5" style={{ backgroundColor: "#25AF9B" }}>
                        <div className="left-panel">
                            <h4>{t('auth.signUp')}</h4>
                            <Image priority src="/assets/images/logos/LM-round.svg" alt="LM" height="350" width="350" />
                        </div>
                    </div>
                    <div className="col-12 col-md-7 d-flex align-items-center justify-content-center py-5">
                        <p className="text-muted">{t('auth.loading')}</p>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default function SignUpPage() {
    return (
        <Suspense fallback={<SignUpFallback />}>
            <SignUpContent />
        </Suspense>
    );
}
